import _ from "lodash";
import { create } from "zustand";

interface GraphStoreType {
  currentFilterList: any;
  graphHeight: number;
  showAsListRowsPerPage: number;
  filterModeStatus: null | "filters" | "columns";
  filterColumnList: GraphListType[];
  getFilterTarget: GraphListType[];
  removeFilterList: any;
  filterTrigger: number;
  filterSearchData: FilterSearchDataType;
  removeFilterTrigger: number;
  resetFilter: boolean;
  setCurrentFilterList: (filterList: any) => void;
  setGraphHeight: (height: number) => void;
  getFilterType: (type: string) => "numeric" | "text" | "NULL";
  getGraphBoxKey: () => string;
  setFilterTarget: (data: GraphListType[], resetFilter?: boolean) => void;
  setRemoveFilterList: (data: any) => void;
  handleFilterModeStatus: (
    view: null | "filters" | "columns",
    allowDup?: boolean
  ) => void;
  setGraphWidth: (key: string, width: number) => void;
  addFilterColumnList: (data: GraphListType[], reset?: boolean) => void;
  setFilterColumnList: (key: string, data: GraphListType) => void;
  delFilterColumnList: (key: string) => void;
  clearFilterColumnList: () => void;
  setFilterColumnListLoading: (key: string, value: boolean) => void;
  setFilterAbortController: (key: string, controller: AbortController) => void;
  setTotalMap: (key: string, totalMap: TotalMapType) => void;
  setSelectedFilter: (key: string, data: any[]) => void;
  setRangeSelectedList: (key: string, data: any[]) => void;
  setLastGraphView: (key: string, view: "list" | "graph") => void;
  setLastUsedFilters: (key: string, data: any[]) => void;
  setExcludeRangeList: (key: string, data: string[]) => void;
  setExcludeValueList: (key: string, data: string[]) => void;
  setUsedRangeList: (key: string, data: string[]) => void;
  setUsedValueList: (key: string, data: string[]) => void;
  setSearchValue: (key: string, value: string) => void;
  setFilterSortBy: (key: string, sort: "key" | "value") => void;
  setFilterSortOrder: (key: string, order: "asc" | "desc") => void;
  setOriginFilterList: (key: string, data: any[]) => void;
  setLastModified: (key: string, date: string, user: string) => void;
  setFilterErrorMsg: (key: string, message?: string) => void;
  updateFilterTrigger: () => void;
  setFilterSearchData: (column: string, search: string) => void;
  removeFilter: () => void;
  resetGraphData: () => void;
}

export interface FilterSearchDataType {
  column: string;
  search: string;
}
interface TotalMapType {
  totalRows: number;
  totalPages: number;
  maxCount: number;
}

export interface FilterType {
  name: string;
  originName: string;
  sourceInfo?: SourceInfoType[];
  displayName?: string;
  filterCount: string | number | null;
  unFilterCount: string | number;
  graphBackgroundCount?: string | number;
}

export interface SessionFilterDataType {
  [key: string]: GraphListType[];
}

export interface SourceInfoType {
  libraryDetailId: string;
  libraryVersion: string;
  libraryName: string;
}

export interface GraphListType {
  id: string;
  graphWidth: number;
  column: string;
  type: string;
  sourceInfo?: SourceInfoType[];
  stepFilterId?: number;
  errorMsg?: string;
  filter?: FilterType[];
  totalMap?: TotalMapType;
  searchValue?: string;
  selectedFilter?: any[];
  lastUsedFilters?: any[];
  rangeSelectedList?: any[]; // list graph 표출시, histogram에서 선택했던 bar들은 따로 분리하기 위한 용도
  excludeRangeList?: string[];
  excludeValueList?: string[];
  originFilterList?: any[];
  usedRangeList?: string[];
  usedValueList?: string[];
  sortBy: "key" | "value";
  sortOrder: "asc" | "desc";
  loadingState?: boolean;
  lastGraphView?: "list" | "graph";
  lastModifiedDate?: string;
  lastModifiedBy?: string;
  invertFlag?: boolean;
  abortController?: AbortController;
}

const useGraphStore = create<GraphStoreType>((set) => ({
  currentFilterList: [],
  graphHeight: 336,
  showAsListRowsPerPage: 1000,
  filterModeStatus: null,
  filterColumnList: [],
  getFilterTarget: [],
  removeFilterList: [],
  filterTrigger: 0,
  removeFilterTrigger: 0,
  filterSearchData: { column: "", search: "" },
  resetFilter: false,
  setCurrentFilterList: (filterList: any) => {
    set({ currentFilterList: filterList });
  },
  setGraphHeight: (height: number) => {
    set({ graphHeight: height });
  },
  getFilterType: (type: string) => {
    if (type) {
      if (
        type.includes("integer") ||
        type.includes("double") ||
        type.includes("decimal") ||
        type.includes("NUMBER")
      ) {
        return "numeric";
      }
      if (type.includes("STRING") || type.includes("DATE")) {
        return "text";
      }
    }
    return "NULL";
  },
  getGraphBoxKey: () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";

    for (let i = 0; i < 12; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      key += characters.charAt(randomIndex);
    }

    return key;
  },
  setFilterTarget: (data: GraphListType[], resetFilter) => {
    set({ getFilterTarget: data, resetFilter });
  },
  setRemoveFilterList: (data: any) => {
    set({ removeFilterList: data });
  },
  handleFilterModeStatus: (
    view: null | "filters" | "columns",
    allowDup?: boolean
  ) => {
    set((state) => {
      if (state.filterModeStatus === view && !allowDup) {
        return { ...state, filterModeStatus: null };
      } else {
        return { ...state, filterModeStatus: view };
      }
    });
  },
  setLastUsedFilters: (key: string, data: any[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          lastUsedFilters: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setRangeSelectedList: (key: string, data: any[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          rangeSelectedList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setLastGraphView: (key: string, view: "list" | "graph") => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          lastGraphView: view,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setUsedRangeList: (key: string, data: string[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          usedRangeList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setExcludeRangeList: (key: string, data: string[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          excludeRangeList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setExcludeValueList: (key: string, data: string[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          excludeValueList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setUsedValueList: (key: string, data: string[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          usedValueList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setSearchValue: (key: string, value: string) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          searchValue: value,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setFilterSortBy: (key: string, sort: "key" | "value") => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          sortBy: sort,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setFilterSortOrder: (key: string, order: "asc" | "desc") => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          sortOrder: order,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setOriginFilterList: (key: string, data: any[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          originFilterList: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setGraphWidth: (key: string, width: number) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          graphWidth: width,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  addFilterColumnList: (data: GraphListType[], reset?: boolean) => {
    set((state) => {
      const baseList = reset ? [] : state.filterColumnList;

      const newData = data.filter(
        (graphData) =>
          !baseList.some((item) => item.column === graphData.column)
      );

      return {
        ...state,
        filterColumnList: [...baseList, ...newData],
      };
    });
  },

  setFilterColumnList: (key: string, data: GraphListType) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      // const type = res.columnInfoList.find((item: any) => {
      //   return item.columnName === key;
      // })?.columnType;

      if (index !== -1) {
        updatedFilterColumnList[index] = data;
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  delFilterColumnList: (key: string) => {
    set((state) => {
      return {
        ...state,
        filterColumnList: state.filterColumnList.filter(
          (data) => data.column !== key
        ),
      };
    });
  },
  clearFilterColumnList: () => {
    set((state) => {
      return {
        ...state,
        filterColumnList: [],
      };
    });
  },
  setFilterColumnListLoading: (key: string, value: boolean) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          loadingState: value,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setFilterAbortController: (key: string, controller: AbortController) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          abortController: controller,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setTotalMap: (key: string, totalMap: TotalMapType) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          totalMap: totalMap,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setSelectedFilter: (key: string, data: any[]) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );
      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          selectedFilter: data,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setLastModified: (key: string, date: string, user: string) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          lastModifiedBy: user,
          lastModifiedDate: date,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  setFilterErrorMsg: (key: string, message?: string) => {
    set((state) => {
      const updatedFilterColumnList = [...state.filterColumnList];
      const index = updatedFilterColumnList.findIndex(
        (item) => item.column === key
      );

      if (index !== -1) {
        updatedFilterColumnList[index] = {
          ...updatedFilterColumnList[index],
          errorMsg: message,
        };
      }
      return {
        ...state,
        filterColumnList: updatedFilterColumnList,
      };
    });
  },
  updateFilterTrigger: () => {
    set((state) => ({ filterTrigger: state.filterTrigger + 1 }));
  },
  removeFilter: () => {
    set((state) => ({ removeFilterTrigger: state.removeFilterTrigger + 1 }));
  },
  setFilterSearchData: (column: string, search: string) => {
    set({ filterSearchData: { column, search } });
  },
  resetGraphData: () => {
    set((state) => {
      return {
        ...state,
        filterModeStatus: null,
        filterColumnList: [],
        getFilterTarget: [],
        filterSearchData: { column: "", search: "" },
      };
    });
  },
}));

export default useGraphStore;
